<script setup lang="ts">
//#region imports
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTypedStore } from '@/store';
import { useLs } from '@/setup/vueLs';
import SubHeader from '@/views/CRM/components/SubHeader.vue';
//#endregion

//#region const
const LS_PATH = 'CRM/siteId';
//#endregion

//#region composables
const store = useTypedStore();
const { t } = useI18n();
const ls = useLs();
//#endregion

//#region state
const currentBrand = ref('');
//#endregion

//#region computed
const brands = computed(() => store.getters['misc/brands']);

const currentRoute = computed(() => {
  const currentRoute = store.getters['nav/getSubView'];
  return t(`crm.menu.${currentRoute}.main`);
});

const activeBrand = computed(() => {
  return brands.value.find((brand) => brand.id === currentBrand.value);
});

const menu = computed(() => {
  return [
    {
      name: t('crm.menu.activities.main'),
      link: '/crm/activities/list',
      permission: 'crm_activities_view',
    },
    {
      name: t('crm.menu.running.main'),
      link: '/crm/running',
      permission: 'crm_activities_run',
    },
    {
      name: t('crm.menu.quality_assurance.main'),
      sections: [
        {
          name: t('crm.menu.quality_assurance.activities'),
          link: '/crm/quality-assurance/activities',
          permission: 'crm_activities_review',
        },
        {
          name: t('crm.menu.quality_assurance.segments'),
          link: '/crm/quality-assurance/segments',
          permission: 'crm_segments_review',
        },
      ],
    },
    {
      name: t('crm.menu.segments.main'),
      link: '/crm/segments',
      permission: 'crm_segments_view',
    },
  ];
});
//#endregion

//#region watch
watch(currentBrand, (newVal) => {
  ls.set(LS_PATH, JSON.stringify(newVal));
});
//#endregion

//#region hooks
currentBrand.value = JSON.parse(ls.get(LS_PATH)) || brands.value[0]!.id;
//#endregion
</script>

<template>
  <div class="crm">
    <SubHeader
      :menu="menu"
      :current-route="currentRoute"
    >
      <div
        v-if="activeBrand"
        class="crm__brand"
      >
        <img
          v-if="activeBrand.favicon_base64"
          :src="activeBrand.favicon_base64"
          :alt="activeBrand.name"
        >
        {{ activeBrand.name }}
        <UiIcon
          name="chevron-down"
          :size="9"
          color="inherit"
        />
        <div class="crm__brand-dropdown">
          <UiScrollbar
            wrap-style="max-height: 400px;"
            :native="false"
          >
            <div
              v-for="brand in brands"
              :key="brand.id"
              class="crm__brand-item"
              :class="{'crm__brand-item_active': currentBrand === brand.id }"
              @click="currentBrand = brand.id"
            >
              <img
                v-if="brand.favicon_base64"
                :src="brand.favicon_base64"
                :alt="brand.name"
              >
              {{ brand.name }}
            </div>
          </UiScrollbar>
        </div>
      </div>
    </SubHeader>
    <div class="crm__wrapper">
      <div class="content-wrapper">
        <RouterView v-slot="{ Component }">
          <component
            :is="Component"
            v-model:current-brand="currentBrand"
          />
        </RouterView>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.crm {
  display: flex;
  flex-direction: column;
  max-height: 100%;
  &__wrapper {
    overflow-y: auto;
  }
  &__brand {
    height: 40px;
    display: flex;
    align-items: center;
    gap: 4px;
    position: relative;
    color: var(--header-text);
    font-size: 14px;
    cursor: pointer;
    padding: 0 12px 0 0;

    img{
      width: 16px;
      height: 16px;
      border-radius: 4px;
    }

    &:hover{
      box-shadow: inset 0 -2px 0 0 var(--border-accent);
    }
    &:hover &{
      &-dropdown {
        display: block;
      }
    }
  }
  &__brand-dropdown {
    display: none;
    position: absolute;
    z-index: 200;
    left: 0;
    box-sizing: border-box;
    min-width: 120px;
    box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.2);
    top: 100%;
    background: var(--header-submenu);
  }
  &__brand-item {
    padding-left: 8px;
    padding-right: 32px;
    height: 34px;

    width: 100%;
    position: relative;
    color: var(--text-body-invert);
    font-size: 14px;
    white-space: nowrap;
    max-width: 400px;
    text-overflow: ellipsis;
    overflow-x: hidden;
    line-height: 2.4;
    display: flex;
    align-items: center;
    gap: 4px;

    &_active{
      color: var(--header-text-active)
    }

    &:hover{
      background-color: var(--header-menu);
    }
  }
}
</style>
